import React, { useState } from 'react';
import { authAPI } from '../api/client';
import useUserStore from '../store/userStore';
import { calcRoot, calcPersonalYear } from '../utils/numbers';
import { C, css } from '../theme';

const CONCERNS = [
  '💰 المال والوضع المادي', '❤️ الحب والعلاقات', '💼 العمل',
  '🌿 الصحة والنفس', '🏠 الأسرة', '✨ الروحانيات', '🔄 قرارات كبرى',
];
const FREQ_NUMS = [
  '1:11 / 11:11','2:22','3:33','4:44','5:55',
  '6:06','7:07','8:08 / 888','9:09 / 999','12:12',
];
const STEPS = [
  { title: '👤 معلوماتك الأساسية', sub: 'نحتاج اسمك وتاريخ ميلادك لحساب جذرك العددي' },
  { title: '🌙 وضعك الحالي', sub: 'تساعد التطبيق على تفسير الأرقام بدقة أكبر لظروفك تحديداً' },
  { title: '🔢 الأرقام التي تراها', sub: 'هل تلاحظ أرقاماً تتكرر في حياتك؟' },
  { title: '✦ ملفك الرقمي', sub: 'هذا ما كشفه كتاب جذر الفلك عنك' },
];

const ROOT_TITLES = {
  1:'قائد الولادة الجديدة',2:'المرآة الأولى للوعي',3:'المُعلِّم الكوني',
  4:'بناء الأساس',5:'رياح التغيير',6:'قلب العلاقات',
  7:'الحكيم الروحي',8:'المدير الأعلى',9:'نهاية الدورة والحكمة',
};
const ROOT_PLANETS = {
  1:'المريخ',2:'القمر',3:'عطارد',4:'الأرض/أورانوس',
  5:'المشتري',6:'الزهرة',7:'نبتون',8:'الزهرة',9:'المريخ',
};
const ROOT_HZ = { 1:'396',2:'417',3:'528',4:'639',5:'741',6:'852',7:'963',8:'111',9:'432' };
const ROOT_CONTRA = { 1:6,2:7,3:4,4:5,5:8,6:1,7:2,8:3,9:1 };

export default function OnboardingScreen({ onNavigate }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const { setAll, token } = useUserStore();

  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [concerns, setConcerns] = useState([]);
  const [status, setStatus] = useState('');
  const [freqNums, setFreqNums] = useState([]);
  const [otherNums, setOtherNums] = useState('');
  const [errors, setErrors] = useState({});

  const root = (day && month && year && year.length === 4)
    ? calcRoot(parseInt(day), parseInt(month), parseInt(year)) : null;
  const personalYear = root
    ? calcPersonalYear(parseInt(day), parseInt(month)) : null;

  function toggleChip(list, setList, val) {
    setList(list.includes(val) ? list.filter(v => v !== val) : [...list, val]);
  }

  function validateStep0() {
    const errs = {};
    if (!name.trim()) errs.name = 'يرجى إدخال اسمك';
    const d = parseInt(day), m = parseInt(month), y = parseInt(year);
    if (!d || !m || !y || d < 1 || d > 31 || m < 1 || m > 12 || y < 1900 || y > 2020)
      errs.dob = 'يرجى إدخال تاريخ ميلاد صحيح';
    if (!gender) errs.gender = 'يرجى اختيار الجنس';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleNext(e) {
    e.preventDefault();
    if (step === 0 && !validateStep0()) return;
    setStep(s => s + 1);
  }

  async function handleFinish(e) {
    e.preventDefault();
    const d = parseInt(day), m = parseInt(month), y = parseInt(year);
    const r = calcRoot(d, m, y);
    setLoading(true);
    try {
      await authAPI.completeOnboarding({
        name: name.trim(), gender,
        birth_day: d, birth_month: m, birth_year: y,
        root_number: r, name_number: r,
        personal_year: calcPersonalYear(d, m),
        concerns: concerns.map(c => c.replace(/^[^\s]+\s/, '')),
        relationship_status: status,
        frequent_numbers: freqNums,
        other_numbers: otherNums,
      });
      const meRes = await authAPI.me();
      setAll(meRes.data.user, token, meRes.data.subscription);
      onNavigate('main');
    } catch (err) {
      alert(err.response?.data?.message || 'حدث خطأ');
    } finally { setLoading(false); }
  }

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        {/* Logo */}
        <div style={s.logo}>
          <span style={{ fontSize: 42, display: 'block', marginBottom: 12, animation: 'glow 3s ease-in-out infinite' }}>✦</span>
          <h1 style={{ fontFamily: 'Amiri, serif', fontSize: 28, color: C.gold, letterSpacing: 3, marginBottom: 6 }}>جذر الفلك</h1>
          <p style={{ fontSize: 12, color: C.text2 }}>أدخل بياناتك لبناء ملفك الرقمي الشخصي</p>
        </div>

        {/* Progress bars */}
        <div style={s.progress}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ ...s.dot, ...(i < step ? s.dotDone : i === step ? s.dotActive : {}) }}>
              {i === step && <div style={s.dotSlide} />}
            </div>
          ))}
        </div>

        {/* Step title */}
        <div style={{ fontFamily: 'Amiri, serif', fontSize: 19, color: C.gold, marginBottom: 5 }}>
          {STEPS[step].title}
        </div>
        <div style={{ fontSize: 12, color: C.text2, marginBottom: 22, lineHeight: 1.65 }}>
          {STEPS[step].sub}
        </div>

        {/* STEP 0 */}
        {step === 0 && (
          <form onSubmit={handleNext}>
            <Field label="الاسم الأول">
              <input style={css.finput} value={name} onChange={e => setName(e.target.value)}
                placeholder="اكتب اسمك..." maxLength={30} />
              {errors.name && <div style={s.err}>{errors.name}</div>}
            </Field>
            <Field label="تاريخ الميلاد">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.4fr', gap: 8 }}>
                <input style={css.finput} value={day} onChange={e => setDay(e.target.value)} placeholder="اليوم" type="number" min="1" max="31" />
                <input style={css.finput} value={month} onChange={e => setMonth(e.target.value)} placeholder="الشهر" type="number" min="1" max="12" />
                <input style={css.finput} value={year} onChange={e => setYear(e.target.value)} placeholder="السنة" type="number" min="1900" max="2020" />
              </div>
              {errors.dob && <div style={s.err}>{errors.dob}</div>}
            </Field>
            <Field label="الجنس">
              <div style={{ display: 'flex', gap: 8 }}>
                {['ذكر','أنثى'].map(g => (
                  <button key={g} type="button" onClick={() => { setGender(g); setErrors(p => ({...p, gender: ''})); }}
                    style={{ ...s.gbtn, ...(gender === g ? s.gbtnSel : {}) }}>
                    {g === 'ذكر' ? '♂ ذكر' : '♀ أنثى'}
                  </button>
                ))}
              </div>
              {errors.gender && <div style={s.err}>{errors.gender}</div>}
            </Field>
            <button style={css.btnPrimary} type="submit">التالي ←</button>
          </form>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <form onSubmit={handleNext}>
            <Field label="ما الذي يشغل تفكيرك أكثر؟">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {CONCERNS.map(c => (
                  <button key={c} type="button"
                    onClick={() => toggleChip(concerns, setConcerns, c)}
                    style={{ ...css.chip, ...(concerns.includes(c) ? css.chipSel : {}) }}>
                    {c}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="الحالة العاطفية (اختياري)">
              <select style={css.finput} value={status} onChange={e => setStatus(e.target.value)}>
                <option value="">اختر...</option>
                <option value="أعزب">أعزب / عزباء</option>
                <option value="في علاقة">في علاقة</option>
                <option value="خطوبة">مخطوب / مخطوبة</option>
                <option value="متزوج">متزوج / متزوجة</option>
                <option value="مفارقة">مفارقة / طلاق</option>
              </select>
            </Field>
            <button style={css.btnPrimary} type="submit">التالي ←</button>
            <button style={css.btnBack} type="button" onClick={() => setStep(0)}>← رجوع</button>
          </form>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <form onSubmit={() => setStep(3)}>
            <Field label="اختر ما تراه بشكل متكرر">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {FREQ_NUMS.map(n => (
                  <button key={n} type="button"
                    onClick={() => toggleChip(freqNums, setFreqNums, n)}
                    style={{ ...css.chip, ...(freqNums.includes(n) ? css.chipSel : {}) }}>
                    {n}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="أرقام أخرى (اختياري)">
              <input style={css.finput} value={otherNums} onChange={e => setOtherNums(e.target.value)}
                placeholder="مثال: 1:33، 2:47..." maxLength={60} />
            </Field>
            <button style={css.btnPrimary} type="button" onClick={() => setStep(3)}>التالي ←</button>
            <button style={css.btnBack} type="button" onClick={() => setStep(1)}>← رجوع</button>
          </form>
        )}

        {/* STEP 3 — Preview */}
        {step === 3 && root && (
          <form onSubmit={handleFinish}>
            <div style={s.preview}>
              <div style={s.prevNum}>{root}</div>
              <div style={s.prevTitle}>{ROOT_TITLES[root]}</div>
              <div style={{ fontSize: 12, color: C.text2 }}>جذرك العددي من كتاب جذر الفلك</div>
              <div style={s.prevStats}>
                {[
                  ['الكوكب', ROOT_PLANETS[root]],
                  ['التردد', `${ROOT_HZ[root]} Hz`],
                  ['الرقم المضاد', ROOT_CONTRA[root]],
                  ['سنتك 2026', personalYear],
                ].map(([lbl, val]) => (
                  <div key={lbl} style={{ textAlign: 'center', padding: '0 12px' }}>
                    <span style={{ fontSize: 10, color: C.text2, display: 'block', marginBottom: 3 }}>{lbl}</span>
                    <span style={{ fontSize: 15, color: C.gold, fontWeight: 600 }}>{val}</span>
                  </div>
                ))}
              </div>
            </div>
            <button style={{ ...css.btnPrimary, opacity: loading ? .6 : 1 }} type="submit" disabled={loading}>
              {loading ? '...' : '✦ ادخل المرصد'}
            </button>
            <button style={css.btnBack} type="button" onClick={() => setStep(2)}>← رجوع وتعديل</button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11, color: '#9B94A8', marginBottom: 7, letterSpacing: .5 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const s = {
  wrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' },
  card: {
    background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 24,
    padding: '40px 32px', maxWidth: 460, width: '100%',
    boxShadow: '0 0 80px rgba(212,168,71,.06), 0 32px 64px rgba(0,0,0,.5)',
    position: 'relative', overflow: 'hidden', animation: 'fadeUp .35s ease',
  },
  logo: { textAlign: 'center', marginBottom: 32 },
  progress: { display: 'flex', gap: 6, marginBottom: 28 },
  dot: { flex: 1, height: 3, background: C.border, borderRadius: 2, position: 'relative', overflow: 'hidden' },
  dotDone: { background: C.goldDim },
  dotActive: { background: C.goldDim },
  dotSlide: { position: 'absolute', inset: 0, background: C.gold, animation: 'slide 1.5s ease-in-out infinite' },
  gbtn: { flex: 1, background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, color: C.text2, fontSize: 14, cursor: 'pointer', transition: 'all .2s', textAlign: 'center' },
  gbtnSel: { background: 'rgba(212,168,71,.1)', borderColor: C.gold, color: C.gold },
  err: { fontSize: 11, color: '#E07070', marginTop: 5 },
  preview: {
    background: `linear-gradient(135deg, ${C.bg3}, ${C.bg4})`,
    border: `1px solid ${C.goldDim}`, borderRadius: 18,
    padding: 24, marginBottom: 8, textAlign: 'center',
  },
  prevNum: { fontFamily: 'Amiri, serif', fontSize: 72, color: C.gold, lineHeight: 1, textShadow: '0 0 40px rgba(212,168,71,.5)', marginBottom: 6 },
  prevTitle: { fontSize: 16, color: C.text, marginBottom: 3, fontWeight: 500 },
  prevStats: { display: 'flex', justifyContent: 'space-around', marginTop: 18, paddingTop: 18, borderTop: `1px solid ${C.border}`, flexWrap: 'wrap', gap: 10 },
};
